"use client";

import { useReducedMotion } from "framer-motion";

import { LazyMotion, MotionDiv } from "@/components/animate";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import {
  navPreviewItem,
  navPreviewMotionProps,
  navPreviewStagger,
} from "./nav-preview-motion";

function toGridGapX(gap: string) {
  return gap.startsWith("gap-x-") ? gap : gap.replace(/^gap-/, "gap-x-");
}

function toGridGapY(gap: string) {
  return gap.startsWith("gap-y-") ? gap : gap.replace(/^gap-/, "gap-y-");
}

export function NavPreviewSubnav({
  label,
  links,
  className,
  columns = 2,
  layout = "columns",
  linkGap = "gap-4",
  columnGap = "gap-8",
  gridRowGap,
  footerLink,
}: {
  label: string;
  links: readonly { label: string; href: string; external?: boolean }[];
  className?: string;
  /** Figma partners preview uses a single column; tools uses two. */
  columns?: 1 | 2;
  /** Grid keeps paired rows aligned when link labels wrap. */
  layout?: "columns" | "grid";
  linkGap?: string;
  columnGap?: string;
  gridRowGap?: string;
  /** Optional trailing CTA (e.g. Tools "View all tools →"). */
  footerLink?: { label: string; href: string };
}) {
  const reduceMotion = useReducedMotion();
  const motionProps = navPreviewMotionProps(reduceMotion);
  const linkColumns = columns === 1 ? [links] : splitIntoColumns(links);
  const useGrid = columns === 2 && layout === "grid";
  const [leftColumn, rightColumn] = useGrid
    ? (linkColumns as [
        { label: string; href: string }[],
        { label: string; href: string }[],
      ])
    : [[], []];
  const gridRowCount = useGrid
    ? Math.max(leftColumn.length, rightColumn.length)
    : 0;
  const resolvedGridRowGap = gridRowGap ?? linkGap;
  const linkVariants = reduceMotion ? undefined : navPreviewItem;

  return (
    <LazyMotion>
      <MotionDiv
        className={cn("flex w-[290px] flex-col gap-4", className)}
        {...motionProps}
      >
        <MotionDiv variants={linkVariants}>
          <p className="text-grey-500 text-ds-body-xs tracking-[-0.24px]">
            {label}
          </p>
        </MotionDiv>
        {columns === 1 ? (
          links.map((link) => (
            <MotionDiv key={link.href + link.label} variants={linkVariants}>
              <NavPreviewSubnavLink link={link} />
            </MotionDiv>
          ))
        ) : useGrid ? (
          <MotionDiv
            className={cn(
              "grid grid-cols-2 items-start",
              toGridGapX(columnGap),
              toGridGapY(resolvedGridRowGap)
            )}
            variants={reduceMotion ? undefined : navPreviewStagger}
          >
            {Array.from({ length: gridRowCount }, (_, rowIndex) => {
              const leftLink = leftColumn[rowIndex];
              const rightLink = rightColumn[rowIndex];

              return [
                leftLink ? (
                  <MotionDiv key={`${rowIndex}-left`} variants={linkVariants}>
                    <NavPreviewSubnavLink link={leftLink} />
                  </MotionDiv>
                ) : null,
                rightLink ? (
                  <MotionDiv key={`${rowIndex}-right`} variants={linkVariants}>
                    <NavPreviewSubnavLink link={rightLink} />
                  </MotionDiv>
                ) : null,
              ];
            })}
          </MotionDiv>
        ) : (
          <MotionDiv
            className={cn("flex", columnGap)}
            variants={reduceMotion ? undefined : navPreviewStagger}
          >
            {linkColumns.flatMap((column) =>
              column.map((link) => (
                <MotionDiv key={link.href + link.label} variants={linkVariants}>
                  <NavPreviewSubnavLink link={link} />
                </MotionDiv>
              ))
            )}
          </MotionDiv>
        )}
        {footerLink ? (
          <MotionDiv variants={linkVariants}>
            <Link
              href={footerLink.href}
              className="text-grey-500 text-ds-body-sm hover:text-pulse-700 block tracking-[-0.28px] transition-colors"
            >
              {footerLink.label}
            </Link>
          </MotionDiv>
        ) : null}
      </MotionDiv>
    </LazyMotion>
  );
}

function NavPreviewSubnavLink({
  link,
}: {
  link: { label: string; href: string; external?: boolean };
}) {
  return (
    <Link
      href={link.href}
      external={link.external}
      className="text-grey-950 text-ds-body-sm hover:text-pulse-700 block leading-[1.45] tracking-[-0.28px] transition-colors"
    >
      {link.label}
    </Link>
  );
}

function splitIntoColumns<T>(items: readonly T[]): [T[], T[]] {
  const midpoint = Math.ceil(items.length / 2);
  return [items.slice(0, midpoint), items.slice(midpoint)];
}
